import {
  ArrowUpRight,
  Calculator,
  ClipboardCheck,
  DoorOpen,
  ListChecks,
  Sparkles,
  Wrench,
} from "lucide-react";
import { Eyebrow, Item, Reveal, Stagger } from "../components/Reveal";
import { useLeadModal } from "../components/LeadModal";

const TAGS_MANUTENCAO = ["Elétrica", "Hidráulica", "Pintura", "Marcenaria", "Pequenos reparos"];

const IMG_DESOCUPACAO =
  "https://images.unsplash.com/photo-1663625318264-695d2d04f11a?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_ORCAMENTOS =
  "https://images.unsplash.com/photo-1648201637025-1c77b9be3013?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_GESTAO =
  "https://images.unsplash.com/photo-1542744173-05336fcc7ad4?crop=entropy&cs=srgb&fm=jpg&q=85";

const CardShell = ({ children, className = "", testId }) => (
  <div
    data-testid={testId}
    className={`group relative h-full overflow-hidden rounded-3xl transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 ${className}`}
  >
    {children}
  </div>
);

const BgImage = ({ src, alt }) => (
  <>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/80 to-white" />
  </>
);

export const Services = () => {
  const { openModal } = useLeadModal();

  return (
    <section id="servicos" className="relative py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>Serviços</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-petrol sm:text-3xl lg:text-4xl">
            Tudo para resolver o que <span className="text-brand">o imóvel precisa.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            A manutenção é só uma das frentes. A Resolv é uma plataforma completa de gestão de
            demandas imobiliárias.
          </p>
        </Reveal>

        <Stagger className="mt-14 grid gap-5 lg:grid-cols-12">
          <Item className="lg:col-span-7">
            <CardShell
              testId="service-card-manutencao"
              className="bg-petrol text-white shadow-[0_30px_60px_-25px_rgba(11,25,44,0.5)]"
            >
              <div className="grid h-full sm:grid-cols-[1.1fr_0.9fr]">
                <div className="p-8 lg:p-10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                    <Wrench className="h-5 w-5" strokeWidth={1.9} />
                  </span>
                  <p className="mt-6 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-brand-400">
                    Frente principal
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-bold">Manutenção</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    Elétrica, hidráulica, pintura, marcenaria, pequenos reparos e demais
                    necessidades do imóvel — com profissionais acionados e acompanhados pela
                    Resolv.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {TAGS_MANUTENCAO.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/70"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="relative min-h-[220px] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Profissional executando serviço de manutenção elétrica"
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-petrol via-petrol/40 to-transparent" />
                </div>
              </div>
            </CardShell>
          </Item>

          <Item className="lg:col-span-5">
            <CardShell
              testId="service-card-desocupacao"
              className="border border-slate-200/80 bg-white hover:border-brand/40 hover:shadow-[0_24px_48px_-20px_rgba(255,92,0,0.25)]"
            >
              <BgImage src={IMG_DESOCUPACAO} alt="Caixas de mudança no chão de um imóvel vazio" />
              <div className="relative p-8 lg:p-10">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <DoorOpen className="h-5 w-5" strokeWidth={1.9} />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-petrol">Desocupação</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Organização e acompanhamento das demandas identificadas durante a saída do
                  imóvel — do levantamento à entrega pronta para o próximo contrato.
                </p>
              </div>
            </CardShell>
          </Item>

          <Item className="lg:col-span-4">
            <CardShell
              testId="service-card-vistorias"
              className="border border-slate-200/80 bg-white hover:border-brand/40 hover:shadow-[0_24px_48px_-20px_rgba(255,92,0,0.25)]"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1748027869634-fc2e545cfb0c?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Profissional realizando vistoria em imóvel"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
              </div>
              <div className="relative p-7 pt-2">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <ClipboardCheck className="h-5 w-5" strokeWidth={1.9} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-petrol">Vistorias</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Vistorias e identificação de necessidades de manutenção e reparos, com registro
                  organizado.
                </p>
              </div>
            </CardShell>
          </Item>

          <Item className="lg:col-span-4">
            <CardShell
              testId="service-card-orcamentos"
              className="border border-slate-200/80 bg-white hover:border-brand/40 hover:shadow-[0_24px_48px_-20px_rgba(255,92,0,0.25)]"
            >
              <BgImage src={IMG_ORCAMENTOS} alt="Prancheta com calculadora e documento de orçamento" />
              <div className="relative p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <Calculator className="h-5 w-5" strokeWidth={1.9} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-petrol">Orçamentos</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Levantamento, organização e acompanhamento de custos. Nada avança sem aprovação
                  clara de quem decide.
                </p>
              </div>
            </CardShell>
          </Item>

          <Item className="lg:col-span-4">
            <CardShell
              testId="service-card-gestao"
              className="border border-slate-200/80 bg-white hover:border-brand/40 hover:shadow-[0_24px_48px_-20px_rgba(255,92,0,0.25)]"
            >
              <BgImage src={IMG_GESTAO} alt="Pessoa acompanhando um dashboard em tela de computador" />
              <div className="relative p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <ListChecks className="h-5 w-5" strokeWidth={1.9} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-petrol">Gestão de serviços</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Acompanhamento da execução até a conclusão, com status claros em cada etapa da
                  demanda.
                </p>
              </div>
            </CardShell>
          </Item>

          <Item className="lg:col-span-12">
            <CardShell
              testId="service-card-personalizadas"
              className="border border-brand/25 bg-gradient-to-r from-brand/[0.06] via-white to-brand/[0.08] hover:border-brand/50 hover:shadow-[0_24px_48px_-20px_rgba(255,92,0,0.3)]"
            >
              <div className="flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center lg:p-10">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand text-white">
                  <Sparkles className="h-5 w-5" strokeWidth={1.9} />
                </span>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-petrol">
                    Soluções personalizadas
                  </h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                    Estruturação de operações específicas para imobiliárias e administradoras: fluxos,
                    SLAs e rede de prestadores desenhados para a sua operação.
                  </p>
                </div>
                <button
                  onClick={() => openModal("", "agency")}
                  data-testid="services-custom-cta"
                  className="group/btn inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-petrol px-6 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand"
                >
                  Montar minha operação
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </button>
              </div>
            </CardShell>
          </Item>
        </Stagger>
      </div>
    </section>
  );
};
